import { create } from "zustand";

/**
 * Store transitório (NÃO persistido) para a "barra sob o cursor" por gráfico —
 * fonte de dados do painel Info.
 *
 * Vive FORA do useChartsStore de propósito: o publish acontece no mousemove do
 * chart (cadência já throttled a ~60fps pelo ChartCanvas), e um store isolado
 * garante que esse publish NUNCA notifica os assinantes do chartStore — só o
 * painel Info assina aqui. Preserva I5 (feedback visual nunca trava): nenhum
 * re-render de chart é disparado pelo movimento do mouse.
 *
 * `getCurrentItem` faz snap na barra mais próxima, então `bar` só troca de
 * referência quando o cursor cruza para outra barra — o guard de igualdade
 * abaixo limita o set() a "trocou de barra", não a cada pixel.
 */
export const useHoverInfoStore = create((set, get) => ({
  // chartId do último gráfico sob o cursor. Vale sempre o chart hovered (sem
  // conceito de "select"). Nunca limpo no mouseleave → congela ao sair do chart.
  lastHoveredChartId: null,
  // chartId -> { bar, prevClose, dayPrevClose }
  hovered: {},

  /**
   * @param {string} chartId
   * @param {object} bar objeto da barra sob o cursor (moreProps.currentItem)
   * @param {{prevClose?: number, dayPrevClose?: number}} extras derivados no seam
   */
  setHoveredBar: (chartId, bar, extras = {}) => {
    if (!chartId || !bar) return;
    const state = get();
    const prev = state.hovered[chartId];
    if (state.lastHoveredChartId === chartId && prev?.bar === bar) return; // no-op: mesma barra
    set({
      lastHoveredChartId: chartId,
      hovered: {
        ...state.hovered,
        [chartId]: {
          bar,
          prevClose: extras.prevClose,
          dayPrevClose: extras.dayPrevClose,
        },
      },
    });
  },

  /** @returns {{chartId: string, bar: object, prevClose?: number, dayPrevClose?: number} | null} */
  getHovered: () => {
    const { lastHoveredChartId, hovered } = get();
    if (!lastHoveredChartId) return null;
    const entry = hovered[lastHoveredChartId];
    if (!entry) return null;
    return { chartId: lastHoveredChartId, ...entry };
  },
}));

/**
 * Deriva os extras de variação a partir do array plotado, no momento do hover
 * (chamado só quando a barra muda). Puro e O(n) apenas no bar-change.
 * - prevClose: close da barra imediatamente anterior.
 * - dayPrevClose: close da última barra do dia anterior (fech. do dia anterior).
 * @param {object} bar
 * @param {Array<object>} plotData
 */
export const computeHoverExtras = (bar, plotData) => {
  const extras = { prevClose: undefined, dayPrevClose: undefined };
  if (!bar || !Array.isArray(plotData) || plotData.length === 0) return extras;

  const i = plotData.indexOf(bar);
  if (i < 0) return extras;

  if (i > 0) extras.prevClose = plotData[i - 1]?.close;

  const d = toDate(bar.date);
  if (d) {
    const day = d.toDateString();
    for (let j = i - 1; j >= 0; j--) {
      const dj = toDate(plotData[j]?.date);
      if (dj && dj.toDateString() !== day) {
        extras.dayPrevClose = plotData[j]?.close;
        break;
      }
    }
  }

  return extras;
};

const toDate = (v) => {
  if (v instanceof Date) return v;
  if (v == null) return null;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
};
